import type { CutoutDefinition, OptionDB } from "@project/emerge";
import { Sides } from "@project/emerge-types";
import { EdgeType } from "../enum/EdgeType";
import { EndpointType } from "../enum/EndpointType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import type { AngleSectionType } from "../interfaces/AngleSectionType";
import type { CornerType } from "../interfaces/CornerType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import { DefaultGroupName } from "../structure/Rect";

/**
 * Shared edge math for angle sections and L-Ecken (corner braces):
 * - resolve angle-section type (corpus/cover)
 * - corner endpoint handling (L-Ecke skip, cutouts, margin)
 * - `computeAngleSectionBarLengthAndShift` for AngleSectionBuildStep
 * - reference length + skip scan for CornerBraceBuildStep Profi variant
 */

export function resolveAngleSectionsForEdge(
  optionDb: OptionDB,
  settings: FlightCaseSettings,
  edge: FlightCaseBlueprint["edges"][number],
): AngleSectionType {
  let angleSections = settings.exteriorOptions.angleSections;
  if (angleSections.variants && edge.rects[0]) {
    const isDefaultGroup = edge.rects[0].group === DefaultGroupName;
    const code = isDefaultGroup
      ? angleSections.variants.corpusCode
      : angleSections.variants.coverCode;
    const codeTrimmed = typeof code === "string" ? code.trim() : "";
    if (codeTrimmed && codeTrimmed !== "__none__") {
      angleSections = optionDb.getByName<AngleSectionType>(
        LTCaseUserOption.AngleSectionType,
        code,
      );
    }
  }
  return angleSections;
}

function getRelevantPlanes(
  edge: FlightCaseBlueprint["edges"][number],
): Sides[] {
  if (edge.dir === "x") return [Sides.LEFT, Sides.RIGHT];
  if (edge.dir === "y") return [Sides.TOP, Sides.BOTTOM];
  return [Sides.FRONT, Sides.BACK];
}

function computeCutByCorner(
  corner: NonNullable<
    FlightCaseBlueprint["edges"][number]["endPoints"][number]["corner"]
  >,
  relevantPlanes: Sides[],
): number {
  const cutoutsArr = corner.partSelection?.cutouts as
    | CutoutDefinition[]
    | undefined;
  if (!cutoutsArr || !Array.isArray(cutoutsArr)) return 0;
  return Math.max(
    0,
    ...cutoutsArr
      .filter((c) => relevantPlanes.includes(c.side))
      .map((c) => c.cut ?? 0),
  );
}

/**
 * Same mm basis as corner L-variant / AngleSection skip: inner span minus half profile extra.
 * (Matches CornerBraceBuildStep bracketLength check and CornerType `bracketLength` semantics.)
 */
export function edgeReferenceLengthForBracketMm(
  edge: FlightCaseBlueprint["edges"][number],
  settings: FlightCaseSettings,
): number {
  return edge.innerLength - settings.exteriorOptions.profiles.extra / 2;
}

function computeProfileAdjustment(settings: FlightCaseSettings): number {
  return (
    settings.exteriorOptions.profiles.height +
    settings.exteriorOptions.profiles.extra / 2
  );
}

function handleCornerEndpoint(
  endPoint: FlightCaseBlueprint["edges"][number]["endPoints"][number],
  edge: FlightCaseBlueprint["edges"][number],
  settings: FlightCaseSettings,
  angleSections: AngleSectionType,
): { diff: number; skipEdge: boolean } {
  const angleVariant = (
    endPoint.corner?.partSelection as CornerType | undefined
  )?.angleVariant;
  if (
    angleVariant &&
    edge.rects[0] &&
    edgeReferenceLengthForBracketMm(edge, settings) ===
      angleVariant.bracketLength
  ) {
    if (endPoint.corner) endPoint.corner.useLVariant = true;
    return { diff: 0, skipEdge: true };
  }
  const relevantPlanes = getRelevantPlanes(edge);
  const cutByCorner = endPoint.corner
    ? computeCutByCorner(endPoint.corner, relevantPlanes)
    : 0;
  const diff = Math.max(angleSections.margin || 0, cutByCorner);
  return { diff, skipEdge: false };
}

/**
 * Winkelprofil-Länge und Positionsversatz entlang der Kante (Außenmaß minus Eck-/Profil-Kürzungen).
 * Mutiert ggf. `endPoint.corner.useLVariant` über `handleCornerEndpoint`.
 */
export function computeAngleSectionBarLengthAndShift(args: {
  edge: FlightCaseBlueprint["edges"][number];
  settings: FlightCaseSettings;
  angleSections: AngleSectionType;
}): { length: number; shift: number; skipEdge: boolean } {
  const { edge, settings, angleSections } = args;
  let length = edge.outerLength;
  let shift = 0;
  const endPoints = edge.endPoints.slice(0, 2);
  let pointIndex = 0;
  let skipEdge = false;

  for (const endPoint of endPoints) {
    const sign = pointIndex === 0 ? 1 : -1;

    if (endPoint.type === EndpointType.ON_CORNER) {
      const cornerResult = handleCornerEndpoint(
        endPoint,
        edge,
        settings,
        angleSections,
      );
      if (cornerResult.skipEdge) {
        skipEdge = true;
        break;
      }
      length -= cornerResult.diff;
      shift += (sign * cornerResult.diff) / 2;
    } else {
      const adjustment = computeProfileAdjustment(settings);
      length -= adjustment;
      shift += (sign * adjustment) / 2;
    }
    pointIndex++;
  }

  return { length, shift, skipEdge };
}

/**
 * Kantenlänge für L-Ecken-Profi (`angleVariant.minLength`): dieselbe Bezugsgröße wie bei der
 * Ecke-L-Variante (`bracketLength`), nicht die kumulative Außenkanten-Länge des Winkelprofils.
 *
 * `lengthAfterEndpoint[0]` und `[1]` sind bewusst gleich — beide Halterungen an einer Kante
 * nutzen dieselbe strukturelle Kantenlänge (wie `edge.innerLength - extra/2`).
 *
 * Walk der Endpunkte nur noch für `skipEdge` (Kugel-L-Ecke / kein Winkelprofil auf der Kante).
 */
export function computeAngleBarLengthAfterEachEndpoint(args: {
  edge: FlightCaseBlueprint["edges"][number];
  settings: FlightCaseSettings;
  angleSections: AngleSectionType;
}): { skipEdge: boolean; lengthAfterEndpoint: [number, number] } {
  const { edge, settings, angleSections } = args;
  const empty: [number, number] = [0, 0];

  if (edge.type === EdgeType.FROMCUT || !edge.rects[0]) {
    return { skipEdge: false, lengthAfterEndpoint: empty };
  }

  const endPoints = edge.endPoints.slice(0, 2);

  for (const endPoint of endPoints) {
    if (endPoint.type === EndpointType.ON_CORNER) {
      const cornerResult = handleCornerEndpoint(
        endPoint,
        edge,
        settings,
        angleSections,
      );
      if (cornerResult.skipEdge) {
        return { skipEdge: true, lengthAfterEndpoint: empty };
      }
    }
  }

  const referenceMm = edgeReferenceLengthForBracketMm(edge, settings);
  return {
    skipEdge: false,
    lengthAfterEndpoint: [referenceMm, referenceMm],
  };
}
